import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { MessageSquare } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../config';

const MAX_STATUS_CHECK_ATTEMPTS = 20;

const CenteredLoading = ({ message, description }) => (
    <div className="h-full w-full flex items-center justify-center">
        <div className="flex flex-col items-center justify-center text-center">
            <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
            <p className="text-lg text-purple-400 mt-4">{message}</p>
            {description && <p className="text-sm text-gray-400 mt-2">{description}</p>}
        </div>
    </div>
);

const ChatView = ({ currentPaper, authToken }) => {
    const [question, setQuestion] = useState('');
    const [chatHistory, setChatHistory] = useState([]);
    const [isAskingQuestion, setIsAskingQuestion] = useState(false);
    const [chatCache, setChatCache] = useState({});
    const [currentChatId, setCurrentChatId] = useState(null);
    const [isCreatingChat, setIsCreatingChat] = useState(false);
    const [newChatTitle, setNewChatTitle] = useState('');
    const [chatStatusCheckAttempts, setChatStatusCheckAttempts] = useState(0);
    const [chatStatusCheckFailed, setChatStatusCheckFailed] = useState(false);
    const queryClient = useQueryClient();

    // Reset attempts when paper changes
    useEffect(() => {
        setChatStatusCheckAttempts(0);
        setChatStatusCheckFailed(false);
    }, [currentPaper]);

    // Chat status query
    const { data: chatStatus } = useQuery({
        queryKey: ['chatStatus', currentPaper?.id],
        queryFn: async () => {
            if (!authToken || !currentPaper) return null;

            const response = await fetch(`${API_URL}/papers/${currentPaper.id}/chat/status`, {
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch chat status');
            }

            const data = await response.json();

            if (data.status === 'processing' && chatStatusCheckAttempts < MAX_STATUS_CHECK_ATTEMPTS) {
                setChatStatusCheckAttempts(prev => prev + 1);
                throw new Error('Still processing');
            }

            if (data.status === 'processing' && chatStatusCheckAttempts >= MAX_STATUS_CHECK_ATTEMPTS) {
                setChatStatusCheckFailed(true);
                return { status: 'error', message: 'Processing timeout' };
            }

            if (data.status === 'completed' || data.status === 'error') {
                setChatStatusCheckAttempts(0);
                setChatStatusCheckFailed(false);
            }

            return data;
        },
        retry: true,
        retryDelay: 20000,
        enabled: !!authToken && !!currentPaper,
    });

    // Fetch chats query
    const { data: paperChats = [], isLoading: isLoadingChats } = useQuery({
        queryKey: ['paperChats', currentPaper?.id],
        queryFn: async () => {
            if (!authToken || !currentPaper) return [];

            const response = await fetch(`${API_URL}/papers/${currentPaper.id}/chats`, {
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                }
            });

            if (!response.ok) throw new Error('Failed to fetch chats');
            const data = await response.json();

            return data.chats && typeof data.chats === 'object'
                ? Object.entries(data.chats).map(([id, chat]) => ({
                    _id: id,
                    title: chat.title || 'New Chat',
                    updated_at: chat.updated_at || new Date().toISOString(),
                    messages: chat.messages || [],
                    isOptimistic: false
                }))
                : [];
        },
        enabled: !!authToken && !!currentPaper
    });

    // Chat messages query
    const { data: currentChatMessages = [], isLoading: isLoadingMessages } = useQuery({
        queryKey: ['chatMessages', currentChatId],
        queryFn: async () => {
            if (!authToken || !currentChatId) return [];

            const response = await fetch(`${API_URL}/papers/${currentPaper.id}/chats/${currentChatId}/messages`, {
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                }
            });

            if (!response.ok) throw new Error('Failed to fetch chat messages');
            const data = await response.json();
            const messages = data.messages || [];

            setChatCache(prev => ({
                ...prev,
                [currentChatId]: messages
            }));

            return messages;
        },
        enabled: !!authToken && !!currentChatId,
        refetchInterval: currentChatId === currentPaper?.currentChatId ? 1000 : false,
    });

    // Create chat mutation
    const createChatMutation = useMutation({
        mutationFn: async (title) => {
            const optimisticChat = {
                _id: 'temp-' + Date.now(),
                title: title || 'New Chat',
                updated_at: new Date().toISOString(),
                messages: [],
                isOptimistic: true
            };

            queryClient.setQueryData(['paperChats', currentPaper?.id], (old = []) => {
                return [...(old || []), optimisticChat];
            });

            const response = await fetch(`${API_URL}/chats/create`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    paper_id: currentPaper.id,
                    title: title || 'New Chat'
                })
            });

            if (!response.ok) {
                queryClient.setQueryData(['paperChats', currentPaper?.id], (old = []) => {
                    return (old || []).filter(chat => chat._id !== optimisticChat._id);
                });
                const error = await response.json();
                throw new Error(error.detail || 'Failed to create chat');
            }

            const { chat_id } = await response.json();
            return { chat_id, optimisticId: optimisticChat._id, title };
        },
        onSuccess: (data) => {
            queryClient.setQueryData(['paperChats', currentPaper?.id], (old = []) => {
                return (old || []).map(chat =>
                    chat._id === data.optimisticId
                        ? {
                            _id: data.chat_id,
                            title: data.title,
                            updated_at: new Date().toISOString(),
                            messages: [],
                            isOptimistic: false
                        }
                        : chat
                );
            });

            setChatCache(prev => ({
                ...prev,
                [data.chat_id]: []
            }));

            setCurrentChatId(data.chat_id);
            setIsCreatingChat(false);
            setNewChatTitle('');
        }
    });

    // Delete chat mutation
    const deleteChatMutation = useMutation({
        mutationFn: async (chatId) => {
            const response = await fetch(`${API_URL}/papers/${currentPaper.id}/chat/${chatId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                }
            });

            if (!response.ok) {
                throw new Error('Failed to delete chat');
            }

            return chatId;
        },
        onSuccess: (chatId) => {
            queryClient.setQueryData(['paperChats', currentPaper?.id], (old = []) =>
                old.filter(chat => chat._id !== chatId)
            );

            if (currentChatId === chatId) {
                setCurrentChatId(null);
                setChatHistory([]);
            }
        }
    });

    // Send message mutation
    const sendMessageMutation = useMutation({
        mutationFn: async ({ message, chatId, paperId }) => {
            const response = await fetch(`${API_URL}/chats/${chatId}/messages`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    message,
                    paper_id: paperId
                })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || 'Failed to send message');
            }

            return await response.json();
        },
        onSuccess: (data) => {
            if (data.response) {
                setChatHistory(prev => [...prev, {
                    type: 'ai',
                    content: data.response
                }]);
            }
        },
        onError: (error) => {
            console.error('Failed to send message:', error);
            setChatHistory(prev => [...prev, {
                type: 'error',
                content: 'Failed to send message. Please try again.'
            }]);
        }
    });

    const handleCreateChat = () => {
        if (!currentPaper || createChatMutation.isLoading) {
            return;
        }
        createChatMutation.mutate(newChatTitle || 'New Chat');
    };

    const handleAskQuestion = async () => {
        if (!question.trim() || !currentPaper || !currentChatId) {
            return;
        }

        const newQuestion = question;
        setQuestion('');
        setIsAskingQuestion(true);

        setChatHistory(prev => [...prev, { type: 'user', content: newQuestion }]);

        try {
            const result = await sendMessageMutation.mutateAsync({
                message: newQuestion,
                chatId: currentChatId,
                paperId: currentPaper.id
            });

            if (result.response) {
                setChatCache(prev => ({
                    ...prev,
                    [currentChatId]: [
                        ...(prev[currentChatId] || []),
                        { role: 'user', content: newQuestion },
                        { role: 'assistant', content: result.response }
                    ]
                }));
            }
        } finally {
            setIsAskingQuestion(false);
        }
    };

    // Clear chat history when switching chats
    useEffect(() => {
        setChatHistory([]);
        setQuestion('');
    }, [currentChatId]);

    // Cleanup effect
    useEffect(() => {
        return () => {
            setChatCache({});
        };
    }, []);

    const isChatInputDisabled = !currentChatId || isAskingQuestion || !question.trim();

    // Render the chat interface
    return (
        <div className="h-full flex">
            {/* Chat List Sidebar */}
            <div className="w-64 border-r border-purple-900/30 bg-black/50 overflow-y-auto">
                <div className="p-4">
                    <Button
                        onClick={() => setIsCreatingChat(true)}
                        className="w-full mb-4 bg-purple-600/20 text-purple-400 hover:bg-purple-600/30 
                        transition-colors duration-200 rounded-xl px-6 py-2.5 flex items-center 
                        justify-center gap-2 border border-purple-500/20 disabled:opacity-50 
                        disabled:cursor-not-allowed"
                        disabled={createChatMutation.isLoading}
                    >
                        {createChatMutation.isLoading ? (
                            <>
                                <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                                Creating...
                            </>
                        ) : (
                            <>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M12 5v14M5 12h14" />
                                </svg>
                                New Chat
                            </>
                        )}
                    </Button>

                    {/* Chat List */}
                    <div className="space-y-2">
                        {isLoadingChats ? (
                            <div className="text-center py-4">
                                <div className="w-6 h-6 border-2 border-purple-400 border-t-transparent rounded-full animate-spin mx-auto" />
                                <p className="text-sm text-gray-400 mt-2">Loading chats...</p>
                            </div>
                        ) : paperChats.length === 0 ? (
                            <div className="text-center py-4">
                                <p className="text-sm text-gray-400">No chats yet</p>
                            </div>
                        ) : (
                            paperChats.map((chat) => (
                                <div
                                    key={chat._id}
                                    className={`p-3 rounded-lg ${chat.isOptimistic
                                        ? 'opacity-50 cursor-wait'
                                        : currentChatId === chat._id
                                            ? 'bg-purple-600/20 border border-purple-500/50'
                                            : 'hover:bg-purple-600/10'
                                        }`}
                                >
                                    <div className="flex justify-between items-start">
                                        <div
                                            className="flex-1 cursor-pointer"
                                            onClick={() => !chat.isOptimistic && setCurrentChatId(chat._id)}
                                        >
                                            <h3 className="text-sm font-medium text-purple-400 truncate">
                                                {chat.title}
                                                {chat.isOptimistic && ' (Creating...)'}
                                            </h3>
                                            <div className="flex justify-between items-center mt-1">
                                                <p className="text-xs text-gray-400">
                                                    {new Date(chat.updated_at).toLocaleDateString()}
                                                </p>
                                                {chat.isOptimistic && (
                                                    <div className="w-3 h-3 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                                                )}
                                                {!chat.isOptimistic && chat.messages && (
                                                    <span className="text-xs text-gray-500">
                                                        {chat.messages.length} messages
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        {!chat.isOptimistic && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (window.confirm('Are you sure you want to delete this chat?')) {
                                                        deleteChatMutation.mutate(chat._id);
                                                    }
                                                }}
                                                className="ml-2 p-1 text-gray-400 hover:text-red-400 transition-colors"
                                                title="Delete chat"
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="16"
                                                    height="16"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                >
                                                    <path d="M3 6h18" />
                                                    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                                                    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                                                    <line x1="10" y1="11" x2="10" y2="17" />
                                                    <line x1="14" y1="11" x2="14" y2="17" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>

            {/* Chat Content */}
            <div className="flex-1 flex flex-col">
                {!chatStatus ? (
                    <CenteredLoading
                        message="Generating Embeddings..."
                        description="Please wait while we prepare the chat feature."
                    />
                ) : chatStatus.status === 'error' || chatStatusCheckFailed ? (
                    <div className="max-w-3xl mx-auto">
                        <div className="text-center py-8">
                            <div className="text-red-400 mb-3">
                                <svg className="w-12 h-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <p className="text-lg text-red-400">Chat Feature Unavailable</p>
                            <p className="text-sm text-gray-400 mt-2">
                                We encountered an error while preparing the chat feature.
                                Please try refreshing the page or contact support if the issue persists.
                            </p>
                        </div>
                    </div>
                ) : chatStatus.status === 'processing' ? (
                    <div className="max-w-3xl mx-auto">
                        <div className="text-center py-8">
                            <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                            <p className="text-lg text-purple-400">Preparing Chat Feature</p>
                            <p className="text-sm text-gray-400 mt-2">
                                Our AI is analyzing the paper to enable intelligent conversations.
                                This may take a few minutes.
                            </p>
                            <p className="text-xs text-gray-500 mt-2">
                                Attempt {chatStatusCheckAttempts + 1} of {MAX_STATUS_CHECK_ATTEMPTS}
                            </p>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Chat History */}
                        <div className="flex-1 overflow-y-auto p-6">
                            <div className="max-w-3xl mx-auto space-y-6">
                                <div className="space-y-4">
                                    {!currentChatId ? (
                                        <div className="text-center text-gray-400 py-8">
                                            <p>Select or create a chat to start the conversation</p>
                                        </div>
                                    ) : isLoadingMessages ? (
                                        <div className="flex items-center justify-center h-full py-12">
                                            <div className="flex flex-col items-center gap-4">
                                                <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
                                                <p className="text-gray-400">Loading conversation...</p>
                                            </div>
                                        </div>
                                    ) : currentChatMessages.length === 0 && chatHistory.length === 0 ? (
                                        <div className="text-center text-gray-400 py-8">
                                            <p>Start a conversation about this paper with our AI assistant.</p>
                                        </div>
                                    ) : (
                                        <>
                                            {currentChatMessages.map((message, index) => (
                                                <div
                                                    key={`message-${index}`}
                                                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                                >
                                                    <div
                                                        className={`max-w-[80%] p-4 rounded-lg ${message.role === 'user'
                                                            ? 'bg-purple-600/20 text-purple-200'
                                                            : message.role === 'error'
                                                                ? 'bg-red-900/20 text-red-200'
                                                                : 'bg-gray-800/50 text-gray-200'
                                                            }`}
                                                    >
                                                        {message.role !== 'user' && (
                                                            <div className="text-xs text-gray-400 mb-1">
                                                                {message.role === 'assistant' ? 'AI Assistant' : 'Error'}
                                                            </div>
                                                        )}
                                                        <div className="whitespace-pre-wrap">{message.content}</div>
                                                    </div>
                                                </div>
                                            ))}

                                            {chatHistory.map((message, index) => (
                                                <div
                                                    key={`pending-${index}`}
                                                    className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                                                >
                                                    <div
                                                        className={`max-w-[80%] p-4 rounded-lg ${message.type === 'user'
                                                            ? 'bg-purple-600/20 text-purple-200'
                                                            : message.type === 'error'
                                                                ? 'bg-red-900/20 text-red-200'
                                                                : 'bg-gray-800/50 text-gray-200'
                                                            }`}
                                                    >
                                                        {message.type !== 'user' && (
                                                            <div className="text-xs text-gray-400 mb-1">
                                                                {message.type === 'ai' ? 'AI Assistant' : 'Error'}
                                                            </div>
                                                        )}
                                                        <div className="whitespace-pre-wrap">{message.content}</div>
                                                    </div>
                                                </div>
                                            ))}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Question Input */}
                        <div className="p-6 bg-black border-t border-purple-900/30">
                            <div className="max-w-3xl mx-auto">
                                <div className="p-4 bg-purple-900/10 rounded-lg border border-purple-900/20">
                                    {!currentChatId ? (
                                        <div className="text-center text-gray-400">
                                            <p>Select or create a chat to start the conversation</p>
                                        </div>
                                    ) : (
                                        <div className="flex gap-3">
                                            <input
                                                type="text"
                                                value={question}
                                                onChange={(e) => setQuestion(e.target.value)}
                                                placeholder="Ask anything about this paper..."
                                                className="flex-1 px-4 py-2 bg-black/50 border border-purple-900/30 rounded-lg 
                                                text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50
                                                disabled:opacity-50 disabled:cursor-not-allowed"
                                                disabled={!currentChatId || isAskingQuestion}
                                                onKeyPress={(e) => {
                                                    if (e.key === 'Enter' && !e.shiftKey) {
                                                        e.preventDefault();
                                                        handleAskQuestion();
                                                    }
                                                }}
                                            />
                                            <Button
                                                onClick={handleAskQuestion}
                                                disabled={isChatInputDisabled}
                                                className="bg-purple-600/20 text-purple-400 hover:bg-purple-600/30 rounded-xl px-6 py-2.5 
                                                flex items-center gap-2 transition-colors duration-200 disabled:opacity-50 
                                                disabled:cursor-not-allowed"
                                            >
                                                {isAskingQuestion ? (
                                                    <>
                                                        <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                                                        Thinking...
                                                    </>
                                                ) : (
                                                    <>
                                                        <MessageSquare className="w-4 h-4" />
                                                        Send
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* New Chat Modal */}
            {isCreatingChat && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center">
                    <div className="bg-gray-900 p-6 rounded-lg w-96">
                        <h2 className="text-xl font-semibold text-purple-400 mb-4">
                            New Chat
                        </h2>
                        <input
                            type="text"
                            value={newChatTitle}
                            onChange={(e) => setNewChatTitle(e.target.value)}
                            placeholder="Enter chat title..."
                            className="w-full px-4 py-2 bg-black/50 border border-purple-900/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50"
                            disabled={createChatMutation.isLoading}
                        />
                        {createChatMutation.error && (
                            <p className="text-red-400 text-sm mt-2">
                                {createChatMutation.error.message}
                            </p>
                        )}
                        <div className="flex justify-end gap-3 mt-4">
                            <Button
                                onClick={() => {
                                    setIsCreatingChat(false);
                                    setNewChatTitle('');
                                    createChatMutation.reset();
                                }}
                                variant="ghost"
                                disabled={createChatMutation.isLoading}
                                className="px-4 py-2 text-gray-400 hover:text-gray-300 transition-colors"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleCreateChat}
                                disabled={createChatMutation.isLoading || !newChatTitle.trim()}
                                className="bg-purple-600/20 text-purple-400 hover:bg-purple-600/30 
                                transition-colors duration-200 rounded-xl px-6 py-2.5 
                                flex items-center gap-2 border border-purple-500/20 
                                disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {createChatMutation.isLoading ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M12 5v14M5 12h14" />
                                        </svg>
                                        Create Chat
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChatView; 